import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MagnifyingGlass as Search,
  ArrowRight,
  CaretDown as ChevronDown,
  List,
  Phone,
} from '@phosphor-icons/react';
import { getTheme, getThemeStyles } from '../../VoiceWidget/themes';
import { MorphIcon } from '../../VoiceWidget/MorphIcon';
import { CurvedTabs } from '../../ui/CurvedTabs';
import { DatePicker } from '../../ui/DatePicker';
import { Chip } from '../../ui/Chip';
import { Input } from '../../ui/input';
import { LeaseCategoryCard } from './LeaseCategoryCard';
import { AndysSiteMenu } from './AndysSiteMenu';
import { AndysPopularCars } from './AndysPopularCars';
// Bundled Andys logo — imported (not a public path) so Vite emits a
// base-correct URL that loads in BOTH the standalone preview and the widget
// overlay, and the Andys landing ALWAYS shows the Andys logo regardless of
// which org the ambient page resolved to (#1108). 240 px WebP, ≈11 KB: it is
// inlined into the bundle, so it has to stay small (#1469).
import andysLogo from '../../../assets/dealers/andys-logo.webp';
import {
  ANDYS_HERO_IMAGE,
  ANDYS_HERO_VIDEO,
  HERO_VIDEO_DELAY_MS,
  shouldPlayHeroVideo,
} from './andysHeroMedia';
import { ANDYS_LEASE_CATEGORIES } from './andysLeaseCategories';
import {
  ANDYS_RENTAL_ENGINE_URL,
  ANDYS_RENT_DEFAULT_LOCATION,
  ANDYS_RENT_LOCATIONS,
} from './andysRentLocations';
import { ANDYS_DEFAULT_MENU_ITEMS, buildAndysBfcUrl } from './andysSiteMenuItems';
import { ANDYS_DEFAULT_TAB, ANDYS_TABS } from './andysLandingTabs';
import {
  DEFAULT_RENT_TIME,
  RENT_TIMES,
  addDaysISO,
  daysBetween,
  firstPickupISO,
  isSundayISO,
  todayISO,
} from '../../../utils/andysDates';
import './AndysBuyRentLease.css';

/**
 * AndysBuyRentLease — «разводящий экран» Andys (issue #1108): табы
 * Buy · Rent · Lease, «Ask AI» на каждом табе, BFC-баннер (только Buy, #1469).
 *
 * ПРЕЗЕНТАЦИОННЫЙ компонент: вся связь с внешним миром — через колбэки, чтобы
 * его можно было гонять в Storybook и не тащить провайдеры/сеть. Контейнер
 * (widget-view overlay / preview-route) прокидывает реальные реализации.
 *
 * @param {object} props
 * @param {number|null} [props.carCount] — живой размер инвентаря → "Browse N cars".
 * @param {Array<object>} [props.popularCars] — слайды «Popular now» на Buy
 *   (#1469), готовит контейнер (`buildPopularCarSlides`): свободные hot offers в
 *   порядке каталога, до 10. Пусто (грузится / машин нет) — секции нет.
 * @param {(ctx: {tab: string, query?: string, category?: object}) => void} [props.onAskAI]
 *   — хендофф в чат Janette с текущим контекстом.
 * @param {(query: string) => void} [props.onOpenCatalog] — открыть mini-catalog (Buy).
 * @param {(params: object) => void} [props.onOpenRental] — уйти в движок Wheelsystems (Rent).
 * @param {(category: object|null) => void} [props.onOpenLease] — «Browse all»:
 *   уйти на страницу лизинга дилера со ВСЕМИ вариантами. Категорию НЕ несёт —
 *   у дилерской /leasing/ нет фильтра по типу (#1248).
 * @param {(category: object) => void} [props.onReserveLease] — «Reserve» на
 *   выбранной категории: открыть дилерскую форму лизинговой заявки
 *   (form-template `leasing_booking`, #1256).
 * @param {string} [props.phone] — телефон салона для tel: ссылки.
 * @param {Array<{id: string, label: string, href: string}>} [props.menuItems]
 *   — пункты меню сайта (☰ в шапке, #1469). href'ы origin-aware собирает
 *   контейнер (`buildAndysMenuItems`); по умолчанию — абсолютные прод-URL.
 *   Пустой массив прячет кнопку меню.
 * @param {string} [props.primaryColor] — резолвнутый primary дилера (prod Andys
 *   token → #E31837). Если не передан — Andys preset red (#c73030). Лендинг
 *   ВСЕГДА Andys-брендовый, НЕ наследует ambient WIDGET_STYLE (иначе на demo/
 *   preview он оранжевый — дефолт demo-shift-gears, #1108).
 * @param {(event: string, params: object) => void} [props.onTrack] — аналитика
 *   кликов (#1482): `sg_landing_*` с параметрами события и активным `tab`;
 *   у каждого ухода с лендинга есть `destination`. Зовётся ДО колбэка
 *   перехода. В beacon их шлёт контейнер (services/landingEvents.js).
 * @param {string} [props.rentalTermsUrl] — куда ведёт «Rental terms & conditions»
 *   под формой Rent (#1469): страница аренды дилера — на ней его условия
 *   аренды и страховки. Origin-aware передаёт контейнер.
 */
const noop = () => {};

// Bands are Anton's (#1469, 2026-10-07): 23 / 24–69 / 70+. The engine's
// youngest driver is 23. `value` goes to the prefill bridge, which sends its
// low bound to the engine's single-year select (`mapAge`). Engine prices,
// checked live the same day: 23 pays a young-driver surcharge, 24–70 the
// standard price, 71+ a senior surcharge. Andys is moving that surcharge to
// 70 in Wheelsystems to match these bands; until then "70+" searches at the
// standard price.
/**
 * Возврат в день выдачи — только позже неё (#1469): равное или более раннее
 * время возврата сдвигаем на следующие полчаса. После последнего времени
 * (23:00) в тот же день некуда — возврат переносится на следующий день.
 */
const nextRentTime = (t) => RENT_TIMES.find((x) => x > t);
const fixSameDayReturn = (r) => {
  if (!r.from || r.to !== r.from || r.toTime > r.fromTime) return r;
  const next = nextRentTime(r.fromTime);
  return next ? { ...r, toTime: next } : { ...r, to: addDaysISO(r.from, 1) };
};

// Воскресенье у Andys вне графика доставки (Mon–Sat 09:30–23:00, их условия
// аренды): такую бронь могут перенести или отменить с полным возвратом. Выбрать
// его можно — пометка в календаре и под датами (#1469).
const SUNDAY_NOTE =
  'Andys delivers Mon–Sat, so a Sunday booking may be moved or cancelled with a full refund.';

const DRIVER_AGES = [
  { value: '23', label: '23' },
  { value: '24-69', label: '24 – 69' },
  { value: '70+', label: '70+' },
];

// British-Forces banner deep-link. Баннер живёт только на Buy (#1469), поэтому
// пер-табовые ссылки на leasing / car-rental (#1108) больше не нужны. Это
// дефолт без резолвнутого сайта (прод) — контейнер передаёт ссылку на сайт
// текущего окружения.
const ANDYS_BFC_URL = buildAndysBfcUrl();

const BUY_CHIPS = ['Hybrid', 'Yaris', 'Honda', 'SUV'];

// id панели меню — на него ссылается aria-controls кнопки ☰.
const SITE_MENU_ID = 'andys-site-menu';

/**
 * AndysSelect — кастомный dropdown, открывается ПОД триггером и рендерится
 * inline в DOM (без portal), поэтому работает в Shadow DOM и не страдает
 * багом нативного `<select>` (popup «над» селектом). #1108.
 */
const AndysSelect = ({
  id,
  value,
  onChange,
  options,
  ariaLabel,
  placeholder = '',
  invalid = false,
  describedBy,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // composedPath, НЕ contains(e.target): в Shadow DOM у document-листенера
      // e.target ретаргетится на shadow host → contains(host) всегда false →
      // селект закрывался на ЛЮБОМ тапе, в т.ч. по опции → локация не
      // выбиралась вообще (#1248).
      const path = e.composedPath ? e.composedPath() : [e.target];
      if (ref.current && !path.includes(ref.current)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape closes only the dropdown: the overlay ignores a handled Escape
      // and would otherwise close the whole landing too (#1469).
      e.preventDefault();
      setOpen(false);
    };
    // pointerdown + отложенное подключение: тап, ОТКРЫВШИЙ дропдаун, не должен
    // тут же его закрыть на touch (#1248).
    const t = setTimeout(() => document.addEventListener('pointerdown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="andys-brl__select" ref={ref}>
      <button
        type="button"
        id={id}
        className={`andys-brl__select-trigger${invalid ? ' andys-brl__select-trigger--invalid' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={`andys-brl__select-value${selected ? '' : ' andys-brl__select-value--placeholder'}`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className="andys-brl__select-arrow"
          size={18}
          weight="regular"
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul className="andys-brl__select-menu" role="listbox">
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`andys-brl__select-option${o.value === value ? ' andys-brl__select-option--sel' : ''}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AndysBuyRentLease = ({
  carCount = null,
  popularCars = [],
  onOpenCatalog = noop,
  onOpenRental = noop,
  onOpenLease = noop,
  onReserveLease = noop,
  onAskAI = noop,
  onTrack = noop,
  phone = '+35725579380',
  menuItems = ANDYS_DEFAULT_MENU_ITEMS,
  primaryColor = null,
  leaseCategories = ANDYS_LEASE_CATEGORIES,
  logoUrl = null,
  heroImageUrl = ANDYS_HERO_IMAGE,
  heroVideoUrl = ANDYS_HERO_VIDEO,
  bfcUrl = ANDYS_BFC_URL,
  rentalTermsUrl = ANDYS_RENTAL_ENGINE_URL,
}) => {
  // Config-overridable (#1108 follow-up): default = hardcoded config file; a
  // mount/config override (passed by the container) can supply per-dealer
  // categories without a redeploy.
  const categories =
    Array.isArray(leaseCategories) && leaseCategories.length
      ? leaseCategories
      : ANDYS_LEASE_CATEGORIES;
  const [tab, setTab] = useState(ANDYS_DEFAULT_TAB);

  // Every event carries the tab it happened on (#1482).
  const track = (event, params = {}) => onTrack(event, { tab, ...params });

  // CurvedTabs зовёт onChange и на тапе по активному табу — это не переход.
  const changeTab = (next) => {
    if (next === tab) return;
    track('sg_landing_tab', { from: tab, to: next });
    setTab(next);
  };

  // Меню сайта (#1469): лендинг теперь главная, с него уходят на остальные
  // страницы. Фокус после закрытия возвращаем на ☰ — кроме ухода по ссылке.
  const hasMenu = Array.isArray(menuItems) && menuItems.length > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeMenu = useCallback(({ restoreFocus = true } = {}) => {
    setMenuOpen(false);
    if (restoreFocus) menuButtonRef.current?.focus();
  }, []);

  // Andys-брендовая палитра: red primary + светлые нейтрали. Пиним явно, чтобы
  // лендинг не наследовал ambient WIDGET_STYLE (demo-shift-gears orange по
  // умолчанию, #1108). Если контейнер отдал резолвнутый цвет дилера — он в
  // приоритете (prod Andys #E31837), иначе Andys preset red.
  const themeStyle = useMemo(() => {
    const base = getTheme('andys', 'light');
    return getThemeStyles(primaryColor ? { ...base, primaryColor } : base);
  }, [primaryColor]);

  const toggleMenu = () => {
    if (!menuOpen) track('sg_landing_menu', { action: 'open' });
    setMenuOpen(!menuOpen);
  };

  // Buy
  const [buyQuery, setBuyQuery] = useState('');
  // Есть что искать → показываем круглую кнопку поиска и уводим в неё иконку
  // (#1269). Клик по чипу подставляет текст в это же поле (#1248), поэтому
  // «выбран чип» отдельным состоянием не является — условие одно.
  const hasBuyQuery = buyQuery.trim() !== '';
  // Последний чип — только для аналитики: ищут ровно его → `fromChip` (#1482).
  const [chipQuery, setChipQuery] = useState(null);

  // Rent
  const [rent, setRent] = useState({
    location: ANDYS_RENT_DEFAULT_LOCATION,
    from: '',
    to: '',
    // Driver age is NOT pre-selected — the user picks it (was '30-65', #1196),
    // and must: it sets the price (#1469, see DRIVER_AGES).
    age: '',
    oneWay: false,
    // Точка возврата — только при «Return to a different location» (#1469).
    dropoff: '',
    // Время выдачи и возврата — в тех же date picker'ах; по умолчанию полдень,
    // как в движке. От него зависит цена: 09:00 → 18:00 на неделю — это 8 дней.
    fromTime: DEFAULT_RENT_TIME,
    toTime: DEFAULT_RENT_TIME,
    // Купон движка — поле по галочке «I have a coupon» (#1469).
    hasCoupon: false,
    coupon: '',
  });
  // «Check availability» без возраста: группа подсвечена как ошибка, пока не
  // выберут (#1469). Поиск стартует уже на странице движка, где возраст по
  // умолчанию 25 — для 23 и 71+ это цена без их надбавки.
  const [ageMissing, setAgeMissing] = useState(false);
  const ageGroupRef = useRef(null);
  // One-way без точки возврата — та же подсветка, что у возраста (#1469).
  const [dropoffMissing, setDropoffMissing] = useState(false);
  const dropoffFieldRef = useRef(null);
  // Галочку купона поставили — фокус в появившееся поле. Не autoFocus: панель
  // Rent монтируется заново при каждой смене вкладки, и с поставленной
  // галочкой возврат на Rent сам открывал бы клавиатуру на телефоне.
  // Ищем поле внутри поля возраста — атом <Input> ref не пробрасывает.
  const ageFieldRef = useRef(null);
  useEffect(() => {
    if (rent.hasCoupon) ageFieldRef.current?.querySelector('.andys-brl__coupon-input')?.focus();
  }, [rent.hasCoupon]);

  // Точки возврата — все, кроме точки выдачи: «вернуть туда же» — это не one-way.
  const dropoffOptions = ANDYS_RENT_LOCATIONS.filter((l) => l.value !== rent.location).map(
    (loc) => ({ value: loc.value, label: loc.label })
  );
  const setPickupLocation = (v) =>
    setRent((r) => ({ ...r, location: v, dropoff: r.dropoff === v ? '' : r.dropoff }));
  // Как в движке Wheelsystems, время выдачи переносится во время возврата —
  // иначе легко случайно заплатить лишние сутки. Но только пока возврат его
  // повторяет: выбранное вручную время возврата не трогаем.
  const setPickupTime = (t) =>
    setRent((r) =>
      fixSameDayReturn({ ...r, fromTime: t, toTime: r.toTime === r.fromTime ? t : r.toTime })
    );
  // Выдача сдвинулась позже возврата — возврат сбрасываем.
  const setPickupDate = (v) =>
    setRent((r) => fixSameDayReturn({ ...r, from: v, to: r.to && v && r.to < v ? '' : r.to }));
  const setReturnDate = (v) => setRent((r) => fixSameDayReturn({ ...r, to: v }));

  // Первый день выдачи — послезавтра, как в календаре самого движка Andys (его
  // `leadDays` = 2): раньше bridge не запустит поиск, а прошлые даты не дают
  // машин (#1255). Считаем per-render (не useMemo) — дёшево и не устаревает
  // через полночь при долго открытом лендинге.
  const rentMinDate = firstPickupISO();
  // Return — не раньше pick-up; после выдачи в 23:00 — со следующего дня.
  // Ограничение по времени — только в сам день выдачи.
  const returnMinDate =
    rent.from && !nextRentTime(rent.fromTime)
      ? addDaysISO(rent.from, 1)
      : rent.from || rentMinDate;
  const sundayPicked = isSundayISO(rent.from) || isSundayISO(rent.to);

  // Lease (#1256): выбор категории раскрывает CTA «Reserve» → форма enquiry
  // (дилерский form-template `leasing_booking`, открывает контейнер). Отдельный
  // «Browse all» в футере уводит на страницу лизинга дилера со всеми вариантами.
  // В #1240 форму снесли целиком и оставили только уход на /leasing/ — это было
  // расхождение со спекой, здесь возвращаем оба действия.
  const [leaseCategory, setLeaseCategory] = useState(null);

  const browseLabel = carCount != null ? `Browse ${carCount} cars` : 'Browse cars';

  // Поиск с учётом введённого — круглая кнопка или Enter в поле.
  const searchCatalog = (via) => {
    track('sg_landing_search', {
      destination: 'catalog',
      query: buyQuery.trim(),
      fromChip: chipQuery !== null && buyQuery === chipQuery,
      via,
    });
    onOpenCatalog(buyQuery);
  };

  const pickChip = (chip) => {
    track('sg_landing_chip', { chip });
    setBuyQuery(chip);
    setChipQuery(chip);
  };

  // Баннер и стрелка — одна ссылка в новой вкладке.
  const trackBfc = () => track('sg_landing_bfc_click', { destination: 'bfc' });

  const handleRentSubmit = () => {
    if (rent.oneWay && !rent.dropoff) {
      track('sg_landing_rent_blocked', { reason: 'dropoff' });
      setDropoffMissing(true);
      dropoffFieldRef.current?.querySelector('#andys-rent-drop')?.focus();
      return;
    }
    if (!rent.age) {
      track('sg_landing_rent_blocked', { reason: 'age' });
      setAgeMissing(true);
      ageGroupRef.current?.querySelector('[role="radio"]')?.focus();
      return;
    }
    const location = ANDYS_RENT_LOCATIONS.find((l) => l.value === rent.location);
    const dropoff = rent.oneWay
      ? ANDYS_RENT_LOCATIONS.find((l) => l.value === rent.dropoff)
      : null;
    // Пустой купон при поставленной галочке — просто без купона.
    const coupon = rent.hasCoupon ? rent.coupon.trim() : '';
    // Длина аренды и как далеко pick-up — в днях; без дат null (#1482).
    track('sg_landing_rent_search', {
      destination: 'rental',
      location: rent.location,
      oneWay: rent.oneWay,
      dropoff: dropoff ? rent.dropoff : undefined,
      days: daysBetween(rent.from, rent.to),
      daysToPickup: daysBetween(todayISO(), rent.from),
      ageBand: rent.age,
      // Сам код в аналитику не уходит — только был ли он.
      withCoupon: coupon !== '',
    });
    // Уводим в движок Wheelsystems: config-driven prefill bridge заполнит его
    // форму и запустит поиск. CRM-лида тут нет: в поиске нет ни имени, ни
    // телефона, и API отклонял каждый такой лид (#1469).
    onOpenRental({
      locationCode: location?.engineCode || null,
      locationLabel: location?.label,
      dropoffCode: dropoff?.engineCode || null,
      dropoffLabel: dropoff?.label,
      from: rent.from,
      to: rent.to,
      fromTime: rent.from ? rent.fromTime : undefined,
      toTime: rent.to ? rent.toTime : undefined,
      age: rent.age,
      oneWay: rent.oneWay,
      coupon: coupon || undefined,
    });
  };

  // Клик по плитке ВЫБИРАЕТ категорию (подсветка) и показывает слой «Reserve»
  // над футером — не авто-прыжок и не авто-открытие формы (#1240 → #1256).
  // Повторный клик по выбранной снимает выбор и прячет слой (#1469).
  const toggleCategory = (category) => {
    track('sg_landing_lease_select', {
      category: category.id,
      selected: leaseCategory?.id !== category.id,
    });
    setLeaseCategory((current) => (current?.id === category.id ? null : category));
  };

  // Слой «Reserve» прибит к низу экрана прямо над футером (#1469), поэтому
  // scrollIntoView, который раньше подтягивал инлайн-кнопку под сеткой (#1256),
  // больше не нужен: слой виден при любом положении скролла.
  const showReserve = tab === 'lease' && leaseCategory != null;

  // Резерв контента под фиксированный футер — по его настоящей высоте (#1469).
  // Константа 80px врала: на узком экране «Check availability» переносится в
  // две строки, футер вырастает до 96px, и последняя строка формы («Rental
  // terms & conditions») оставалась под ним даже в самом низу страницы. Замер
  // включает слой Reserve и safe-area; до него и без ResizeObserver — прежняя
  // оценка из CSS. ResizeObserver — футер меняется без ремаунта: смена таба,
  // слой Reserve, поворот экрана.
  const cardRef = useRef(null);
  const footerRef = useRef(null);
  useEffect(() => {
    const card = cardRef.current;
    const footer = footerRef.current;
    if (!card || !footer || typeof ResizeObserver === 'undefined') return undefined;
    const write = () => {
      const h = Math.ceil(footer.getBoundingClientRect().height);
      if (h > 0) card.style.setProperty('--andys-footer-measured', `${h}px`);
    };
    write();
    const observer = new ResizeObserver(write);
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  // Видео в шапке включаем через HERO_VIDEO_DELAY_MS после показа лендинга, а
  // не на первой отрисовке (#1469): иначе оно делит канал телефона с бандлом,
  // инвентарём и фото. До старта и без видео (экономия трафика, медленная сеть,
  // reduced motion) виден тот же кадр — фото фоном шапки.
  const [heroVideoOn, setHeroVideoOn] = useState(false);
  useEffect(() => {
    if (!heroVideoUrl || !shouldPlayHeroVideo()) return undefined;
    const timer = setTimeout(() => setHeroVideoOn(true), HERO_VIDEO_DELAY_MS);
    return () => clearTimeout(timer);
  }, [heroVideoUrl]);

  return (
    <div className={`andys-brl andys-brl--${tab}${showReserve ? ' andys-brl--reserve' : ''}`} style={themeStyle}>
      <header
        className="andys-brl__hero"
        style={heroImageUrl ? { '--andys-hero-img': `url("${heroImageUrl}")` } : undefined}
      >
        {/* Andys showroom background video (muted autoplay loop). Poster = the
            hero photo so it shows instantly and covers slow/absent video. The
            dark scrim overlay sits above it for text legibility (#1108).
            Mounted only once heroVideoOn — see above (#1469). */}
        {heroVideoUrl && heroVideoOn && (
          <video
            className="andys-brl__hero-video"
            autoPlay
            muted
            loop
            playsInline
            poster={heroImageUrl || undefined}
          >
            <source src={heroVideoUrl} type="video/mp4" />
          </video>
        )}
        <div className="andys-brl__hero-scrim" aria-hidden="true" />

        {/* Header: logo left; call + site menu right, bare white icons (#1108,
            menu — #1469). */}
        <div className="andys-brl__topbar">
          <div className="andys-brl__logo-plate">
            <img className="andys-brl__logo" src={logoUrl || andysLogo} alt="Andy's Motors" />
          </div>
          <div className="andys-brl__topbar-actions">
            <a
              className="andys-brl__icon-btn"
              href={`tel:${phone}`}
              aria-label="Call Andy's Motors"
              onClick={() => track('sg_landing_call', { destination: 'phone' })}
            >
              <Phone size={20} weight="regular" aria-hidden="true" />
            </a>
            {hasMenu && (
              <button
                ref={menuButtonRef}
                type="button"
                className="andys-brl__icon-btn"
                aria-label="Site menu"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                aria-controls={SITE_MENU_ID}
                onClick={toggleMenu}
              >
                <List size={24} weight="regular" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        {/* Single centred heading over the photo, above the card sheet. */}
        <div className="andys-brl__hero-copy">
          <h1 className="andys-brl__headline">
            Buy, rent for the week, or lease long-term
            <span className="andys-brl__headline-line2">
              Same showroom, same family — since 1989.
            </span>
          </h1>
        </div>
      </header>

      <CurvedTabs
        className="andys-brl__tabs"
        ariaLabel="Rent, Buy or Lease"
        tabs={ANDYS_TABS}
        value={tab}
        onChange={changeTab}
      />

      <div ref={cardRef} className="andys-brl__card">
        {/* Scrollable content — only this scrolls when a tab is tall (the lease
            form); hero, tabs and the CTA footer stay fixed (#1108). */}
        <div className="andys-brl__scroll">
        {/* BUY */}
        {tab === 'buy' && (
          <div className="andys-brl__panel andys-brl__panel--buy">
            {/* «Popular now» — над поиском (#1469): лендинг стал главной,
                и первое, что видно на Buy, — живые машины, а не пустое поле. */}
            <AndysPopularCars
              cars={popularCars}
              onCarClick={(car, index) =>
                track('sg_landing_popular_click', {
                  destination: 'catalog',
                  carId: car.id,
                  position: index + 1,
                  price: car.priceValue ?? null,
                })
              }
            />
            {/* British Forces banner — ONLY on Buy (tax-free pricing is about
                buying; on Rent/Lease it was noise) and, since the landing became
                the homepage, compact and UNDER the popular cars: the cars come
                first (#1469). Persistent banner, not a tooltip/alert (#1108).
                Split-pill: banner (rounded left, square right) + 4px gap +
                arrow button (square left, rounded right), equal height. */}
            <div className="andys-brl__bfc-row">
              <a
                className="andys-brl__bfc"
                href={bfcUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackBfc}
              >
                <span className="andys-brl__bfc-text">
                  <span className="andys-brl__bfc-title">
                    Tax-free pricing for British Forces on the BFC programme
                  </span>
                </span>
              </a>
              <a
                className="andys-brl__bfc-btn"
                href={bfcUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="British Forces special terms"
                onClick={trackBfc}
              >
                <ArrowRight size={20} weight="regular" aria-hidden="true" />
              </a>
            </div>
            {/* Поле + круглая кнопка поиска. Кнопка появляется, как только есть
                что искать, и забирает к себе иконку лупы из поля (#1269).
                Иконка — ОДИН абсолютный элемент на всю строку, а не две штуки с
                кросс-фейдом: так это буквально перелёт, а не подмена. */}
            <div className="andys-brl__search-row">
              <div className="andys-brl__search">
                <input
                  id="andys-buy-search"
                  className="andys-brl__search-input"
                  type="text"
                  aria-label="Search cars"
                  placeholder="Search cars — try &quot;Hybrid SUV&quot;"
                  value={buyQuery}
                  onChange={(e) => setBuyQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter прокидывает запрос на сайт дилера и ищет там (#1240).
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      searchCatalog('enter');
                    }
                  }}
                />
              </div>
              {/* Кнопка в DOM всегда, но при пустом запросе схлопнута и
                  `disabled` — иначе иконке некуда лететь (цель перелёта должна
                  существовать до старта анимации), а ширина строки прыгала бы
                  скачком вместо плавного расхождения. `disabled` убирает её и из
                  таб-порядка, то есть для клавиатуры и скринридера её нет. */}
              <button
                type="button"
                className="andys-brl__search-go"
                aria-label="Search"
                disabled={!hasBuyQuery}
                aria-hidden={hasBuyQuery ? undefined : true}
                onClick={() => searchCatalog('button')}
              />
              <span
                className="andys-brl__search-icon"
                data-in-button={hasBuyQuery ? 'true' : 'false'}
                aria-hidden="true"
              >
                <Search size={18} weight="regular" />
              </span>
            </div>
            <div className="andys-brl__chips">
              {BUY_CHIPS.map((chip) => (
                // Клик по чипу ТОЛЬКО подставляет запрос в поле; переход в каталог
                // с применённым фильтром — по нижней кнопке (#1248, откат #1240).
                <Chip key={chip} onClick={() => pickChip(chip)}>
                  {chip}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {/* RENT */}
        {tab === 'rent' && (
          <div className="andys-brl__panel">
            <div className="andys-brl__field">
              <label className="andys-brl__field-label" htmlFor="andys-rent-loc">
                Pick-up location
              </label>
              <AndysSelect
                id="andys-rent-loc"
                ariaLabel="Pick-up location"
                value={rent.location}
                onChange={setPickupLocation}
                options={ANDYS_RENT_LOCATIONS.map((loc) => ({
                  value: loc.value,
                  label: loc.label,
                }))}
              />
              <label className="andys-brl__checkbox">
                <input
                  type="checkbox"
                  checked={rent.oneWay}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setRent((r) => ({ ...r, oneWay: checked }));
                    if (!checked) setDropoffMissing(false);
                  }}
                />
                Return to a different location
              </label>
            </div>

            {/* One-way: поле точки возврата появляется по галочке (#1469). */}
            {rent.oneWay && (
              <div className="andys-brl__field" ref={dropoffFieldRef}>
                <label className="andys-brl__field-label" htmlFor="andys-rent-drop">
                  Drop-off location
                </label>
                <AndysSelect
                  id="andys-rent-drop"
                  ariaLabel="Drop-off location"
                  placeholder="Choose where to return the car"
                  value={rent.dropoff}
                  invalid={dropoffMissing}
                  describedBy={dropoffMissing ? 'andys-rent-drop-error' : undefined}
                  onChange={(v) => {
                    setRent((r) => ({ ...r, dropoff: v }));
                    setDropoffMissing(false);
                  }}
                  options={dropoffOptions}
                />
                {dropoffMissing && (
                  <p id="andys-rent-drop-error" className="andys-brl__field-error" role="alert">
                    Choose the drop-off location.
                  </p>
                )}
              </div>
            )}

            <div className="andys-brl__dates">
              <div className="andys-brl__row">
                <div className="andys-brl__col andys-brl__field">
                  <label className="andys-brl__field-label" htmlFor="andys-rent-from">
                    Pick-up
                  </label>
                  <DatePicker
                    id="andys-rent-from"
                    ariaLabel="Pick-up date and time"
                    min={rentMinDate}
                    value={rent.from}
                    onChange={(e) => setPickupDate(e.target.value)}
                    time={rent.fromTime}
                    onTimeChange={setPickupTime}
                    timeOptions={RENT_TIMES}
                    timeLabel="Pick-up time"
                    note={isSundayISO(rent.from) ? SUNDAY_NOTE : ''}
                  />
                </div>
                <div className="andys-brl__col andys-brl__field">
                  <label className="andys-brl__field-label" htmlFor="andys-rent-to">
                    Return
                  </label>
                  <DatePicker
                    id="andys-rent-to"
                    ariaLabel="Return date and time"
                    align="right"
                    min={returnMinDate}
                    value={rent.to}
                    onChange={(e) => setReturnDate(e.target.value)}
                    time={rent.toTime}
                    onTimeChange={(t) => setRent((r) => ({ ...r, toTime: t }))}
                    timeOptions={RENT_TIMES}
                    minTime={rent.from && returnMinDate === rent.from ? rent.fromTime : undefined}
                    timeLabel="Return time"
                    note={isSundayISO(rent.to) ? SUNDAY_NOTE : ''}
                  />
                </div>
              </div>
              {/* Живой регион всегда в DOM: появившуюся пометку зачитают. */}
              <p className="andys-brl__date-note" role="status">
                {sundayPicked ? SUNDAY_NOTE : ''}
              </p>
            </div>

            {/* Возраст слева, купон справа (#1469): галочка — в строке заголовка
                «Driver age», поле купона под ней — на уровне чипов и той же
                ширины. Не влезают рядом — блок купона целиком переносится под
                чипы: решает сам flex-wrap по ширине содержимого, без
                брейкпоинтов. Купон движка — за галочкой, чтобы не путать тех, у
                кого его нет; неверный код движок сам покажет у себя. */}
            <div ref={ageFieldRef} className="andys-brl__field">
              <div className="andys-brl__age-row">
                <div className="andys-brl__age-pick">
                  <span className="andys-brl__field-label" id="andys-rent-age-label">
                    Driver age
                  </span>
                  <div
                    ref={ageGroupRef}
                    className={`andys-brl__chips${ageMissing ? ' andys-brl__chips--invalid' : ''}`}
                    role="radiogroup"
                    aria-labelledby="andys-rent-age-label"
                    aria-required="true"
                    aria-invalid={ageMissing || undefined}
                    aria-describedby={ageMissing ? 'andys-rent-age-error' : undefined}
                  >
                    {DRIVER_AGES.map((age) => {
                      const isSel = rent.age === age.value;
                      return (
                        <Chip
                          key={age.value}
                          role="radio"
                          aria-checked={isSel}
                          selected={isSel}
                          onClick={() => {
                            setRent((r) => ({ ...r, age: age.value }));
                            setAgeMissing(false);
                          }}
                        >
                          {age.label}
                        </Chip>
                      );
                    })}
                  </div>
                </div>
                <div className="andys-brl__coupon">
                  <label className="andys-brl__checkbox andys-brl__coupon-toggle">
                    <input
                      type="checkbox"
                      checked={rent.hasCoupon}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setRent((r) => ({ ...r, hasCoupon: checked }));
                      }}
                    />
                    I have a coupon
                  </label>
                  {rent.hasCoupon && (
                    <Input
                      type="text"
                      className="andys-brl__coupon-input"
                      aria-label="Coupon code"
                      // Узкое поле под галочкой «I have a coupon»: «Coupon code»
                      // в него не влезает, а слово «coupon» уже есть над ним.
                      placeholder="Enter code"
                      value={rent.coupon}
                      onChange={(e) => {
                        const { value } = e.target;
                        setRent((r) => ({ ...r, coupon: value }));
                      }}
                      maxLength={40}
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      enterKeyHint="done"
                    />
                  )}
                </div>
              </div>
              {ageMissing && (
                <p id="andys-rent-age-error" className="andys-brl__field-error" role="alert">
                  Choose the driver&apos;s age — it sets the price.
                </p>
              )}
            </div>

            {/* Условия аренды дилера — мелкой серой ссылкой под формой (#1469),
                в новой вкладке: лендинг остаётся открытым. Ведёт на страницу
                аренды: там «General terms and conditions» и условия страховки
                (/terms-and-conditions у Andys — политика конфиденциальности). */}
            <a
              className="andys-brl__terms"
              href={rentalTermsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('sg_landing_terms_click', { destination: 'rental_terms' })}
            >
              Rental terms &amp; conditions
            </a>
          </div>
        )}

        {/* LEASE — pick a category, then «Reserve» (a layer pinned above the
            footer) opens the dealer's leasing enquiry form; «Browse all» in
            the footer goes to their /leasing/ page with every option (#1256).
            Cards are toggle buttons in a labelled group, not radios: tapping
            the selected card again clears the choice (#1469). */}
        {tab === 'lease' && (
          <div
            className={`andys-brl__panel${
              leaseCategory ? ' andys-brl__panel--has-reserve' : ''
            }`}
          >
            <div
              className="andys-brl__cat-grid"
              role="group"
              aria-label="Lease category"
            >
              {categories.map((cat) => (
                <LeaseCategoryCard
                  key={cat.id}
                  image={cat.image}
                  label={cat.label}
                  priceFrom={cat.priceFrom}
                  selected={leaseCategory?.id === cat.id}
                  onClick={() => toggleCategory(cat)}
                />
              ))}
            </div>
            {/* Кнопка ПОЯВЛЯЕТСЯ по выбору категории, а фокус остаётся на
                карточке — без живого региона пользователь скринридера не узнает,
                что открылось новое действие. Регион ВСЕГДА в DOM: появись он
                вместе с кнопкой, объявлять было бы уже нечему. Он
                position:absolute, поэтому НЕ становится flex-элементом панели и
                не добавляет лишний gap. #1256 */}
            <span className="andys-brl__sr-live" aria-live="polite">
              {leaseCategory
                ? `${leaseCategory.label} selected — Check availability button at the bottom of the screen`
                : ''}
            </span>
          </div>
        )}
        </div>

        {/* Pinned footer at the very bottom: the in-page "Ask AI" button (opens
            the chat widget) sits next to the primary CTA (#1108). */}
        <div ref={footerRef} className="andys-brl__footer">
          {/* «Reserve» — слой над Ask AI + Browse all, только пока выбрана
              категория (#1469). Живёт ВНУТРИ фиксированного футера: так он
              всегда ровно над ним, без расчёта высоты футера в JS. */}
          {showReserve && (
            <button
              type="button"
              className="andys-brl__cta andys-brl__reserve"
              onClick={() => {
                track('sg_landing_lease_reserve', {
                  destination: 'lease_form',
                  category: leaseCategory.id,
                });
                onReserveLease(leaseCategory);
              }}
            >
              {/* Название и цена уже на выбранной карточке; кнопка — действие (#1469). */}
              Check availability
              <ArrowRight size={20} weight="regular" aria-hidden="true" />
            </button>
          )}
          <div className="andys-brl__footer-actions">
            <button
              type="button"
              className="andys-brl__ask"
              onClick={() => {
                // Тот же контекст, что уходит в чат (handleAskAI контейнера).
                const query = buyQuery.trim();
                track('sg_landing_ask_ai', {
                  destination: 'chat',
                  ...(query ? { query } : {}),
                  ...(leaseCategory ? { category: leaseCategory.id } : {}),
                });
                onAskAI({ tab, query: buyQuery, category: leaseCategory });
              }}
            >
              <MorphIcon size={24} iconSize={20} color="var(--widget-primary, #da3227)" />
              Ask AI
            </button>
            {/* «Browse N cars» = ВЕСЬ каталог, без применения запроса (#1269).
                Поиск с учётом введённого — круглая кнопка у поля. Раньше эта
                кнопка молча применяла запрос, то есть противоречила своему
                названию и была единственной точкой входа в поиск.
                Стиль вторичный (bordered), как «Browse all» на Lease: primary-
                действие таба — круглая кнопка поиска, и footer не должен с ней
                конкурировать. На Lease та же расстановка — primary там Reserve
                (#1256, #1270). */}
            {tab === 'buy' && (
              <button
                type="button"
                className="andys-brl__cta andys-brl__cta--secondary"
                onClick={() => {
                  track('sg_landing_browse', { destination: 'catalog', carCount });
                  onOpenCatalog('');
                }}
              >
                {browseLabel}
                <ArrowRight size={20} weight="regular" aria-hidden="true" />
              </button>
            )}
            {tab === 'rent' && (
              <button
                type="button"
                className="andys-brl__cta"
                onClick={handleRentSubmit}
              >
                Check availability
                <ArrowRight size={20} weight="regular" aria-hidden="true" />
              </button>
            )}
            {/* «Browse all» — все варианты лизинга на сайте дилера. Не зависит от
                выбора категории (в отличие от Reserve), поэтому НЕ disabled, и
                вторичный стиль: primary-действие на табе — Reserve (#1256). */}
            {tab === 'lease' && (
              <button
                type="button"
                className="andys-brl__cta andys-brl__cta--secondary"
                onClick={() => {
                  track('sg_landing_lease_browse', { destination: 'leasing' });
                  onOpenLease(leaseCategory);
                }}
              >
                Browse all
                <ArrowRight size={20} weight="regular" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Шторка меню — на уровне корня, а не внутри шапки: шапка — свой
          stacking-контекст (z-index 2) и не подняла бы панель над табами и
          фиксированным футером. */}
      {hasMenu && (
        <AndysSiteMenu
          id={SITE_MENU_ID}
          open={menuOpen}
          items={menuItems}
          onClose={closeMenu}
          onSelect={(item) =>
            track('sg_landing_menu', {
              action: 'link',
              item: item.id,
              destination: `menu:${item.id}`,
            })
          }
        />
      )}
    </div>
  );
};

export default AndysBuyRentLease;
